import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ScopeProvider, useScope } from './contexts/ScopeContext';
import StickyScopeBar from './components/StickyScopeBar';
import Analytics from './pages/Analytics';
import CollaborationAnalytics from './pages/CollaborationAnalytics';
import QuickRefer from './pages/QuickRefer';
import Users from './pages/Users';
import Insights from './pages/Insights';
import Facilities from './pages/Facilities';
import CustomerData from './pages/CustomerData';
import Overview from './pages/Overview';
import StudyAnalytics from './pages/StudyAnalytics';
import Filters from './pages/Filters';
import Vera from './pages/Vera';
import SpecialReports from './pages/SpecialReports';
import TableauReports from './pages/TableauReports';
import Welcome from './pages/Welcome';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import { ForgotPassword, ResetPassword } from './pages/ResetPassword';
import UserManager from './pages/admin/UserManager';
import RoleMatrix from './pages/admin/RoleMatrix';
import CustomerManager from './pages/admin/CustomerManager';
import DataSources from './pages/admin/DataSources';
import ProcedureDatabase from './pages/admin/ProcedureDatabase';
import AgenticReporting from './pages/admin/AgenticReporting';
import CustomerRating from './pages/admin/CustomerRating';
import SchemaDoc from './pages/docs/SchemaDoc';
import ApiDoc from './pages/docs/ApiDoc';
import VeraMetricsDoc from './pages/docs/VeraMetricsDoc';
import FreshnessBanner from './components/FreshnessBanner';
import GlobalRefreshBanner from './components/GlobalRefreshBanner';
import './App.css';

// Sidebar nav order is intentional: the first group ("scope" — My Customers,
// My Filters, All Facilities) controls *what* the user is looking at; below
// the divider is the actual analytical / data tooling, ordered roughly from
// summarized → raw → conversational.
const NAV_ITEMS = [
  { path: '/',               label: 'My Customers',   icon: '🏠', pageKey: 'welcome' },
  { path: '/filters',        label: 'My Filters',     icon: '🔖', pageKey: 'filters' },
  { path: '/facilities',     label: 'All Facilities', icon: '🏥', pageKey: 'facilities' },
  { divider: true },
  // Scoped reports — when a customer/filter/facility is sticky from My
  // Customers (etc.), this whole block lights up and the active item
  // shows a → so users notice they can hop reports without losing scope.
  { path: '/overview',       label: 'Overview',       icon: '🗂', pageKey: 'overview', scopedReport: true },
  { path: '/customer-data',  label: 'Data Trends',    icon: '📈', pageKey: 'customer-data', scopedReport: true },
  { path: '/collaboration-analytics', label: 'Collaboration Analytics', icon: '🔗', pageKey: 'collaboration-analytics', scopedReport: true },
  { path: '/insights',       label: 'Insights',       icon: '💡', pageKey: 'insights', scopedReport: true },
  { path: '/study-analytics', label: 'DICOM Studies', icon: '🩻', pageKey: 'study-analytics', scopedReport: true },
  { path: '/analytics',      label: 'Raw Data',       icon: '📊', pageKey: 'analytics', scopedReport: true },
  { path: '/quick-refer',    label: 'Quick Refer',    icon: '⚡', pageKey: 'quick-refer', scopedReport: true },
  { path: '/users',          label: 'User Reports',   icon: '👤', pageKey: 'users', scopedReport: true },
  { path: '/tableau-reports', label: 'Tableau Reports', icon: '📋', pageKey: 'tableau-reports', scopedReport: true },
  { divider: true },
  { path: '/vera',           label: 'VERA',           icon: 'V', veraIcon: true, pageKey: 'vera' },
  { path: '/special-reports', label: 'Special Reports', icon: '⭐', pageKey: 'special-reports' },
];

const SCOPE_QUERY_KEY = {
  customer: 'customerId',
  filter: 'filterId',
  facility: 'facilityId',
};

const ADMIN_NAV = [
  { path: '/admin/users',          label: 'Users',               icon: '👥', pageKey: 'admin.users' },
  { path: '/admin/customers',      label: 'Customers',           icon: '🤝', pageKey: 'admin.customers' },
  { path: '/admin/data-sources',   label: 'Data Sources',        icon: '🔄', pageKey: 'admin.data-sources' },
  { path: '/admin/procedures',     label: 'Procedure Database',  icon: '🩺', pageKey: 'admin.procedures' },
  { path: '/admin/reporting',      label: 'Agentic Reporting',   icon: '📑', pageKey: 'admin.reporting' },
  { path: '/admin/customer-rating', label: 'Customer Rating',    icon: '🎚️', pageKey: 'admin.rating' },
  { path: '/admin/roles',          label: 'Roles & Permissions', icon: '🛡️', pageKey: 'admin.roles' },
];

const DOCS_NAV = [
  { path: '/docs/schema', label: 'VitalEngine Schema', icon: '🗄️', pageKey: 'docs' },
  { path: '/docs/api',    label: 'VitalEngine API',    icon: '🔌', pageKey: 'docs' },
  { path: '/docs/vera-metrics', label: 'VERA Metrics', icon: '📐', pageKey: 'docs' },
];

function Shell() {
  const { user, logout, hasPermission } = useAuth();
  const { scope, clearScope } = useScope();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Dividers are pure UI — they have no pageKey to permission-check. Drop
  // any divider that ends up at the very top or bottom (e.g. when the user
  // can't see any of the items in the group above/below it) so we don't
  // render an orphaned line.
  const visibleNav = (() => {
    const filtered = NAV_ITEMS.filter(i => i.divider || hasPermission(i.pageKey));
    while (filtered.length && filtered[0].divider) filtered.shift();
    while (filtered.length && filtered[filtered.length - 1].divider) filtered.pop();
    return filtered.filter((item, idx) =>
      !(item.divider && idx > 0 && filtered[idx - 1].divider)
    );
  })();
  const visibleAdmin = ADMIN_NAV.filter(i => hasPermission(i.pageKey));
  const visibleDocs = DOCS_NAV.filter(i => hasPermission(i.pageKey));

  const scopeIsActive = Boolean(
    scope
    && SCOPE_QUERY_KEY[scope.kind]
    && scope.id != null
  );
  const scopeQuery = scopeIsActive
    ? `?${SCOPE_QUERY_KEY[scope.kind]}=${encodeURIComponent(String(scope.id))}`
    : '';

  const renderNavLink = (item) => {
    const to = item.scopedReport && scopeQuery
      ? `${item.path}${scopeQuery}`
      : item.path;
    return (
      <NavLink
        key={item.path}
        to={to}
        end={item.path === '/'}
        className={({ isActive }) =>
          `sidebar-nav-item ${isActive ? 'active' : ''} ${item.scopedReport && isActive && scopeIsActive ? 'scoped-active' : ''}`
        }
      >
        {({ isActive }) => (
          <>
            <span className={`sidebar-nav-icon ${item.veraIcon ? 'vera-nav-icon' : ''}`}>{item.icon}</span>
            <span className="sidebar-nav-label">{item.label}</span>
            {item.scopedReport && scopeIsActive && isActive && (
              <span className="sidebar-nav-arrow" aria-hidden="true">→</span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  // Split the main nav into "before scoped reports / scoped reports / after"
  // so we can outline the middle group when a sticky scope is active.
  const navChunks = (() => {
    const before = [];
    const scoped = [];
    const after = [];
    let zone = 'before';
    for (const item of visibleNav) {
      if (item.scopedReport) {
        zone = 'scoped';
        scoped.push(item);
        continue;
      }
      if (zone === 'scoped' && !item.scopedReport) zone = 'after';
      if (zone === 'before') before.push(item);
      else after.push(item);
    }
    return { before, scoped, after };
  })();

  const renderNavChunk = (items, keyPrefix) =>
    items.map((item, idx) => (
      item.divider ? (
        <li key={`${keyPrefix}-divider-${idx}`} className="sidebar-divider" aria-hidden="true" />
      ) : (
        renderNavLink(item)
      )
    ));

  return (
    <div className="App">
      <header className="app-header">
        <div className="app-header-brand">
          <img
            src="/vitalengine-logo.png"
            alt="VitalEngine"
            className="app-header-logo"
          />
          <div className="app-header-text">
            <h1>VitalData</h1>
            <p>VitalEngine Data Platform</p>
          </div>
        </div>
        <div className="app-header-user">
          <span className="app-user-name">
            {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email}
          </span>
          <span className="app-user-role">{user?.role}</span>
          <button className="app-logout-btn" onClick={handleLogout}>Sign out</button>
        </div>
      </header>

      <div className="app-container">
        <aside className="sidebar">
          <nav>
            <ul className="sidebar-nav">
              {renderNavChunk(navChunks.before, 'before')}

              {navChunks.scoped.length > 0 && (
                <li
                  className={`sidebar-scoped-reports ${scopeIsActive ? 'is-activated' : ''}`}
                  aria-label={scopeIsActive
                    ? `Scoped reports for ${scope.label || scope.kind}`
                    : 'Reports'}
                >
                  {scopeIsActive && (
                    <div className="sidebar-scoped-reports-caption">
                      <span
                        className="sidebar-scoped-reports-caption-text"
                        title={`Scoped · ${scope.label || scope.kind}`}
                      >
                        Scoped · {scope.label || scope.kind}
                      </span>
                      <button
                        type="button"
                        className="sidebar-scoped-reports-clear"
                        onClick={clearScope}
                        aria-label={`Clear ${scope.kind || 'scope'} filter`}
                        title="Clear this filter"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div className="sidebar-scoped-reports-items">
                    {navChunks.scoped.map(item => renderNavLink(item))}
                  </div>
                </li>
              )}

              {renderNavChunk(navChunks.after, 'after')}

              {visibleAdmin.length > 0 && (
                <>
                  <li className="sidebar-section-header">Admin</li>
                  {visibleAdmin.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
                    >
                      <span className="sidebar-nav-icon">{item.icon}</span>
                      <span className="sidebar-nav-label">{item.label}</span>
                    </NavLink>
                  ))}
                </>
              )}

              {visibleDocs.length > 0 && (
                <>
                  <li className="sidebar-section-header">Documentation</li>
                  {visibleDocs.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
                    >
                      <span className="sidebar-nav-icon">{item.icon}</span>
                      <span className="sidebar-nav-label">{item.label}</span>
                    </NavLink>
                  ))}
                </>
              )}
            </ul>
          </nav>
        </aside>

        <main className="main-content">
          <div className="app-main">
            <GlobalRefreshBanner />
            <FreshnessBanner />
            <StickyScopeBar />
            <Routes>
              <Route path="/" element={<RequirePerm page="welcome"><Welcome /></RequirePerm>} />
              <Route path="/vera" element={<RequirePerm page="vera"><Vera /></RequirePerm>} />
              <Route path="/special-reports" element={<RequirePerm page="special-reports"><SpecialReports /></RequirePerm>} />
              <Route path="/tableau-reports" element={<RequirePerm page="tableau-reports"><TableauReports /></RequirePerm>} />
              <Route path="/analytics" element={<RequirePerm page="analytics"><Analytics /></RequirePerm>} />
              <Route path="/collaboration-analytics" element={<RequirePerm page="collaboration-analytics"><CollaborationAnalytics /></RequirePerm>} />
              <Route path="/insights" element={<RequirePerm page="insights"><Insights /></RequirePerm>} />
              <Route path="/quick-refer" element={<RequirePerm page="quick-refer"><QuickRefer /></RequirePerm>} />
              <Route path="/users" element={<RequirePerm page="users"><Users /></RequirePerm>} />
              <Route path="/overview" element={<RequirePerm page="overview"><Overview /></RequirePerm>} />
              <Route path="/customer-data" element={<RequirePerm page="customer-data"><CustomerData /></RequirePerm>} />
              <Route path="/study-analytics" element={<RequirePerm page="study-analytics"><StudyAnalytics /></RequirePerm>} />
              <Route path="/filters" element={<RequirePerm page="filters"><Filters /></RequirePerm>} />
              <Route path="/facilities" element={<RequirePerm page="facilities"><Facilities /></RequirePerm>} />
              <Route path="/admin/users" element={<RequirePerm page="admin.users"><UserManager /></RequirePerm>} />
              <Route path="/admin/customers" element={<RequirePerm page="admin.customers"><CustomerManager /></RequirePerm>} />
              <Route path="/admin/account-groups" element={<Navigate to="/admin/customers" replace />} />
              <Route path="/admin/data-sources" element={<RequirePerm page="admin.data-sources"><DataSources /></RequirePerm>} />
              <Route path="/admin/procedures" element={<RequirePerm page="admin.procedures"><ProcedureDatabase /></RequirePerm>} />
              <Route path="/admin/reporting" element={<RequirePerm page="admin.reporting"><AgenticReporting /></RequirePerm>} />
              <Route path="/admin/customer-rating" element={<RequirePerm page="admin.rating"><CustomerRating /></RequirePerm>} />
              <Route path="/admin/roles" element={<RequirePerm page="admin.roles"><RoleMatrix /></RequirePerm>} />
              <Route path="/docs" element={<Navigate to="/docs/schema" replace />} />
              <Route path="/docs/schema" element={<RequirePerm page="docs"><SchemaDoc /></RequirePerm>} />
              <Route path="/docs/api" element={<RequirePerm page="docs"><ApiDoc /></RequirePerm>} />
              <Route path="/docs/vera-metrics" element={<RequirePerm page="docs"><VeraMetricsDoc /></RequirePerm>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function RequirePerm({ page, children }) {
  const { hasPermission, loading } = useAuth();
  if (loading) return null;
  if (!hasPermission(page)) {
    return (
      <div className="no-access">
        <h2>Access denied</h2>
        <p>You don't have permission to view this page. Contact your administrator if you believe this is a mistake.</p>
      </div>
    );
  }
  return children;
}

function RequireAuth({ children }) {
  const { authenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="app-loading">Loading…</div>;
  if (!authenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/*" element={<RequireAuth><Shell /></RequireAuth>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScopeProvider>
          <AppRoutes />
        </ScopeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
